import { Input, PolicyDispatchingPermission, assets } from '@pulseops/common'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import PDTable from './components/PDTable'
import PolicyDispatchingDialog from './components/PolicyDispatchingDialog'
import { ActivityIndicator, View, Text } from 'react-native'
import { ZIO } from '@mxt/zio'
import { pipe } from 'fp-ts/function'
import { borderedInputStyle } from './styles/policyDispatchingStyles'
import { PolicyDispatchingService, QuickSearchAckInformation } from './services/PolicyDispatchingService'
import { getPermissions } from './utils/policyDispatchingUtils'

const headCell = [
  { id: 1, label: 'Số hợp đồng' },
  { id: 2, label: 'Ngày phát hành' },
  { id: 3, label: 'Ngày KH ký thư' },
  { id: 4, label: 'Mã số đại lý' },
  { id: 5, label: 'Tên đại lý' },
  { id: 6, label: 'Ngày nhập thư' },
  { id: 7, label: 'Văn phòng nhập thư' },
  { id: 8, label: 'Loại thư' },
  { id: 9, label: 'Ghi chú' }
]

const rowCell = [
  { id: 1, label: 'policyNumber' },
  { id: 2, label: 'releasedDate' },
  { id: 3, label: 'signedDate' },
  { id: 4, label: 'agentCode' },
  { id: 5, label: 'agentName' },
  { id: 6, label: 'enterDate' },
  { id: 7, label: 'mailRoom' },
  { id: 8, label: 'type' },
  { id: 9, label: 'note' }
]

type UpdatedLetterRow = {
  policyNumber: string
  releasedDate: string
  signedDate: string
  agentCode: string
  agentName: string
  enterDate: string
  mailRoom: string
  type: string
  note: string
}

type FormValues = {
  contractNumber: string
}

const toRow = (item: QuickSearchAckInformation): UpdatedLetterRow => ({
  policyNumber: item.contractNumber || '',
  releasedDate: item.policyIssueDate || '',
  signedDate: item.customerSignedDate || '',
  agentCode: item.agentNumber || '',
  agentName: item.agentName || '',
  enterDate: item.letterEntryDate || '',
  mailRoom: item.officeCode || '',
  type: item.letterType || '',
  note: item.remark || ''
})

export default function PDUpdatedLetterScreen() {
  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: {
      contractNumber: ''
    }
  })
  const [rows, setRows] = React.useState<UpdatedLetterRow[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [modalInfo, setModalInfo] = React.useState({ title: '', message: '' })
  const [openModal, setOpenModal] = React.useState(false)
  const permissions = getPermissions()
  const allowSearch = permissions.includes(PolicyDispatchingPermission.QuickSearchSearch)

  const handleSearch = handleSubmit((values) => {
    if (!values.contractNumber) {
      setModalInfo({ title: 'Tìm kiếm', message: 'Vui lòng nhập số hợp đồng trước khi tìm kiếm.' })
      setOpenModal(true)
      return
    }

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.searchQuickAckInformation(values.contractNumber),
      ZIO.tap((response) =>
        ZIO.effectTotal(() => {
          setRows([toRow(response.body)])
          setIsLoading(false)
        })
      ),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          setRows([])
          setIsLoading(false)
          setModalInfo({
            title: 'Tìm kiếm thất bại',
            message: 'Không thể tải thông tin thư xác nhận. Vui lòng thử lại sau.'
          })
          setOpenModal(true)
        })
      ),
      ZIO.unsafeRun({})
    )
  })

  return (
    <View style={{ ...styles.container, position: 'relative' } as any}>
      <PolicyDispatchingDialog
        visible={openModal}
        title={modalInfo.title}
        message={modalInfo.message}
        onClose={() => setOpenModal(false)}
      />
      {isLoading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#ED1B2E" />
        </View>
      )}
      <Text style={styles.title}>Tìm nhanh</Text>
      <View style={styles.wrapper}>
        <Text style={styles.subTitle}>Thông tin thư xác nhận</Text>
        <View style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 16, marginBottom: 24 } as any}>
          <View style={{ width: '20%' } as any}>
            <Controller
              name="contractNumber"
              control={control}
              render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                <Input
                  title={'Số hợp đồng'}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  errorMessage={error?.message}
                  containerStyle={{ flex: 1 }}
                  required
                  filled
                  inputStyle={borderedInputStyle}
                />
              )}
            />
          </View>
          <button
            style={{
              marginTop: 24,
              minHeight: 40,
              ...(isLoading || !allowSearch ? styles.disabledButton : styles.submitButton)
            }}
            type="button"
            onClick={handleSearch}
            disabled={isLoading || !allowSearch}
          >
            <span style={styles.mr_5}>
              <assets.SearchWhite />
            </span>
            Tìm
          </button>
        </View>
        <View>
          <PDTable headCell={headCell} rowCell={rowCell} data={rows} />
        </View>
      </View>
    </View>
  )
}

const styles: Record<string, any> = {
  container: {
    paddingTop: 0,
    paddingRight: 40,
    paddingBottom: 30,
    paddingLeft: 40,
    height: '100%',
    overflow: 'auto'
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '20px'
  },
  wrapper: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
  },
  subTitle: {
    color: '#fff',
    fontSize: '18px',
    fontWeight: 'bold',
    paddingTop: 4,
    paddingRight: 12,
    paddingBottom: 4,
    paddingLeft: 12,
    borderRadius: 4,
    backgroundColor: '#e7000b',
    marginBottom: 16,
    width: 'fit-content'
  },
  submitButton: {
    backgroundColor: '#ED1B2E',
    color: '#fff',
    borderWidth: 0,
    paddingTop: 6,
    paddingRight: 16,
    paddingBottom: 6,
    paddingLeft: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    fontWeight: '600'
  },
  disabledButton: {
    backgroundColor: '#e5e7eb',
    color: '#9ca3af',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#d1d5db',
    paddingTop: 6,
    paddingRight: 16,
    paddingBottom: 6,
    paddingLeft: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    fontWeight: '600',
    cursor: 'not-allowed',
    opacity: 0.7
  },
  mr_5: {
    marginRight: '5px'
  },
  loadingOverlay: {
    position: 'fixed' as const,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    zIndex: 2
  }
}
